import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import {
  DEFAULT_PLACEHOLDER,
  MIN_CHAR_MESSAGE,
  MIN_FILTER_LENGTH,
  NO_RESULTS_MESSAGE,
} from "./constants";
import { SearchInputProps, SelectOption } from "./types";
import { normalizeString } from "./utils";

/**
 * SearchInput component for client-side filtering
 *
 * Accepts a list of options and filters them locally
 * based on the search term
 */
export function SearchInput({
  id,
  disabled = false,
  options,
  selectedOptions = [],
  onOptionsSelect,
  maxVisibleChips = 10,
  placeholderText = DEFAULT_PLACEHOLDER,
  minFilterLength = MIN_FILTER_LENGTH,
  noResultsMessage = NO_RESULTS_MESSAGE,
  minCharMessage = MIN_CHAR_MESSAGE,
  isMultiple = true,
}: SearchInputProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] =
    useState<SelectOption[]>(options);

  // Apply filtering when search term changes
  const applyFiltering = useCallback(
    (term: string) => {
      if (!term || term.length < minFilterLength) {
        return;
      }

      const normalizedTerm = normalizeString(term);

      const filtered = options
        .filter((option: SelectOption) =>
          normalizeString(option.label).includes(normalizedTerm)
        )
        .sort((a: SelectOption, b: SelectOption) =>
          a.label.localeCompare(b.label)
        );

      setFilteredResults(filtered);
    },
    [options, minFilterLength]
  );

  // Filter options when search term changes
  useEffect(() => {
    if (selectedOptions.length === 0 && searchTerm.length === 0) {
      setFilteredResults(options);
      return;
    }
    applyFiltering(searchTerm);
  }, [searchTerm, applyFiltering, selectedOptions, options]);

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setFilteredResults(options);
    onOptionsSelect([]);
  }, [options, onOptionsSelect]);

  // Handler for opening/closing the dropdown
  const handleOpen = useCallback(
    (_: React.SyntheticEvent<Element, Event>, isOpen: boolean) => {
      setOpen(isOpen);

      // When opening dropdown in single-select mode with item selected, show all options
      if (isOpen && !isMultiple && selectedOptions.length > 0) {
        setSearchTerm("");
        setFilteredResults(options);
      }
    },
    [isMultiple, selectedOptions, options]
  );

  // Handler for input changes
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, _reason: string) => {
      setSearchTerm(newInputValue);
    },
    [setSearchTerm]
  );

  // Handler for selecting options
  const handleOptionsChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_event: React.SyntheticEvent, newValue: any, reason: string) => {
      if (reason === "clear") resetSearch();

      if (newValue) {
        const valueToPass = Array.isArray(newValue) ? newValue : [newValue];

        onOptionsSelect(valueToPass);
      }
    },
    [onOptionsSelect, resetSearch]
  );

  // No options text based on current state
  const noOptionsText = useMemo(() => {
    if (searchTerm.length < minFilterLength) return minCharMessage;

    return noResultsMessage;
  }, [searchTerm, minFilterLength, minCharMessage, noResultsMessage]);

  // Determine the value to pass to Autocomplete
  const autocompleteValue = useMemo(() => {
    if (isMultiple) return selectedOptions;

    // For single select, return null instead of undefined when empty
    return selectedOptions.length > 0 ? selectedOptions[0] : null;
  }, [isMultiple, selectedOptions]);

  // Custom renderTags function to show "+X more" chip
  const renderTags = useCallback(
    (
      tagValue: SelectOption | SelectOption[],
      getTagProps: (params: {
        index: number;
      }) => React.HTMLAttributes<HTMLElement>
    ) => {
      let selected = [];

      if (Array.isArray(tagValue)) {
        selected = tagValue;
      } else {
        selected = [tagValue];
      }

      const numTags = selected.length;
      const tagsToShow =
        maxVisibleChips && numTags > maxVisibleChips
          ? maxVisibleChips
          : numTags;

      return (
        <>
          {selected.slice(0, tagsToShow).map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                key={index}
                label={option?.label}
                size="small"
                onDelete={(tagProps as { onDelete?: () => void }).onDelete}
              />
            );
          })}
          {maxVisibleChips && numTags > maxVisibleChips && (
            <Chip
              key="more"
              label={`+${numTags - maxVisibleChips} more`}
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(true);
              }}
              sx={{
                bgcolor: "primary.light",
                color: "primary.contrastText",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "primary.main",
                },
              }}
            />
          )}
        </>
      );
    },
    [maxVisibleChips, setOpen]
  );

  return (
    <Stack direction="column" spacing={2}>
      <Autocomplete
        disablePortal
        id={`${id}-autocomplete`}
        size="small"
        disabled={disabled}
        multiple={isMultiple}
        disableCloseOnSelect={isMultiple}
        disableClearable={!isMultiple}
        autoSelect={false}
        clearOnBlur={false}
        selectOnFocus={false}
        limitTags={maxVisibleChips}
        {...(isMultiple ? { renderValue: renderTags } : {})} // To avoid MUI warning about incompatible types
        value={autocompleteValue}
        options={filteredResults}
        onInputChange={handleInputChange}
        onChange={handleOptionsChange}
        open={open}
        onOpen={(e) => handleOpen(e, true)}
        onClose={(e) => handleOpen(e, false)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        filterOptions={(x) => x} // Disable built-in filtering as we're handling it ourselves
        slotProps={{
          listbox: {
            sx: {
              maxHeight: 200,
              ...lightScrollbarStyle,
            },
          },
          popper: {
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  padding: 8,
                },
              },
            ],
          },
        }}
        renderOption={
          isMultiple
            ? ({ key, ...restOfProps }, option, { selected }) => (
                <li key={key} {...restOfProps}>
                  <Checkbox
                    checked={selected}
                    sx={{ marginRight: 1, padding: 0 }}
                    disableRipple
                  />
                  {option.label}
                </li>
              )
            : undefined
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              !autocompleteValue ||
              (Array.isArray(autocompleteValue) &&
                autocompleteValue.length === 0)
                ? placeholderText
                : ""
            }
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                gap: 0.5,
              },
              // Add styles for popup indicator (dropdown arrow)
              "& .MuiAutocomplete-popupIndicator": {
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
              },
            }}
          />
        )}
        noOptionsText={noOptionsText}
      />
    </Stack>
  );
}
